import { FormEvent, useCallback, useEffect, useState } from "react";
import { CalendarClock, LoaderCircle, Pencil, Plus, UserRoundCheck, X } from "lucide-react";

import {
  createMembership,
  createWorkingHours,
  getTeam,
  updateMembership,
  updateWorkingHours,
} from "./api";
import { TeamSettings } from "./TeamSettings";
import type { Employee, Region, TeamDetail, TeamMembership, WorkingHours } from "./types";

const weekdayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function TeamPanel({
  teamId,
  employees,
  regions,
  canWrite,
  onClose,
  onChanged,
}: {
  teamId: string;
  employees: Employee[];
  regions: Region[];
  canWrite: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const [team, setTeam] = useState<TeamDetail>();
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [mode, setMode] = useState<"team" | "membership" | "hours">();
  const [editingMembership, setEditingMembership] = useState<TeamMembership>();
  const [editingHours, setEditingHours] = useState<WorkingHours>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTeam(await getTeam(teamId));
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [teamId]);
  useEffect(() => void load(), [load]);

  async function refreshed() {
    setMode(undefined);
    setEditingMembership(undefined);
    setEditingHours(undefined);
    await load();
    await onChanged();
  }
  return (
    <div className="client-drawer-backdrop" onMouseDown={onClose}>
      <section
        className="client-drawer workforce-drawer team-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Team details"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="drawer-header">
          <div>
            <span className="eyebrow lime">Team operations</span>
            <h2>{team?.name ?? "Team details"}</h2>
          </div>
          <button className="icon-button" aria-label="Close team details" onClick={onClose}>
            <X />
          </button>
        </header>
        <div className="drawer-body">
          {loading ? (
            <div className="table-state">
              <LoaderCircle className="spin" /> Loading team…
            </div>
          ) : null}
          {failed ? (
            <div className="load-error" role="alert">
              We couldn’t load this team.<button onClick={() => void load()}>Try again</button>
            </div>
          ) : null}
          {team ? (
            <>
              <section className="team-summary">
                <div>
                  <span className={`availability ${team.active ? "active" : ""}`}>
                    {team.active ? "Active" : "Inactive"}
                  </span>
                  <h3>{team.name}</h3>
                  <p>{team.defaultRegionName ?? "No default region configured"}</p>
                </div>
                {canWrite ? (
                  <button
                    className="text-button"
                    onClick={() => setMode(mode === "team" ? undefined : "team")}
                  >
                    <Pencil /> Edit team
                  </button>
                ) : null}
              </section>
              {mode === "team" ? (
                <TeamSettings
                  team={team}
                  regions={regions}
                  onSaved={refreshed}
                  onCancel={() => setMode(undefined)}
                />
              ) : null}

              <section className="team-section">
                <SectionHeading
                  eyebrow="Flexible membership"
                  title={`${team.memberCount} active ${team.memberCount === 1 ? "member" : "members"}`}
                  action={canWrite ? () => setMode("membership") : undefined}
                  label="Add member"
                />
                {mode === "membership" ? (
                  <MembershipForm
                    teamId={team.id}
                    employees={employees}
                    onSaved={refreshed}
                    onCancel={() => setMode(undefined)}
                  />
                ) : null}
                <div className="team-records">
                  {team.memberships.length ? (
                    team.memberships.map((member) =>
                      editingMembership?.id === member.id ? (
                        <MembershipForm
                          key={member.id}
                          teamId={team.id}
                          employees={employees}
                          membership={member}
                          onSaved={refreshed}
                          onCancel={() => setEditingMembership(undefined)}
                        />
                      ) : (
                        <button
                          key={member.id}
                          className="team-record"
                          onClick={() => canWrite && setEditingMembership(member)}
                        >
                          <span className="workforce-icon">
                            <UserRoundCheck />
                          </span>
                          <span>
                            <strong>{member.employeeName}</strong>
                            <small>{formatRange(member.validFrom, member.validUntil)}</small>
                          </span>
                          <span
                            className={`availability ${member.employeeStatus === "ACTIVE" ? "active" : ""}`}
                          >
                            {member.employeeStatus}
                          </span>
                        </button>
                      ),
                    )
                  ) : (
                    <p className="catalog-empty">No membership history yet.</p>
                  )}
                </div>
              </section>

              <section className="team-section">
                <SectionHeading
                  eyebrow="Standard availability"
                  title="Working hours"
                  action={canWrite ? () => setMode("hours") : undefined}
                  label="Add hours"
                />
                {mode === "hours" ? (
                  <HoursForm
                    teamId={team.id}
                    onSaved={refreshed}
                    onCancel={() => setMode(undefined)}
                  />
                ) : null}
                <div className="team-records">
                  {team.workingHours.length ? (
                    team.workingHours.map((hours) =>
                      editingHours?.id === hours.id ? (
                        <HoursForm
                          key={hours.id}
                          teamId={team.id}
                          hours={hours}
                          onSaved={refreshed}
                          onCancel={() => setEditingHours(undefined)}
                        />
                      ) : (
                        <button
                          key={hours.id}
                          className="team-record"
                          onClick={() => canWrite && setEditingHours(hours)}
                        >
                          <span className="workforce-icon">
                            <CalendarClock />
                          </span>
                          <span>
                            <strong>
                              {weekdayNames[hours.weekday]} · {hours.startTime.slice(0, 5)}–
                              {hours.endTime.slice(0, 5)}
                            </strong>
                            <small>{formatRange(hours.effectiveFrom, hours.effectiveUntil)}</small>
                          </span>
                          <span className={`availability ${hours.active ? "active" : ""}`}>
                            {hours.active ? "Active" : "Inactive"}
                          </span>
                        </button>
                      ),
                    )
                  ) : (
                    <p className="catalog-empty">No standard working hours configured.</p>
                  )}
                </div>
              </section>
            </>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function MembershipForm({
  teamId,
  employees,
  membership,
  onSaved,
  onCancel,
}: {
  teamId: string;
  employees: Employee[];
  membership?: TeamMembership;
  onSaved: () => Promise<void>;
  onCancel: () => void;
}) {
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSending(true);
    setFailed(false);
    const fields = new FormData(event.currentTarget);
    try {
      if (membership)
        await updateMembership(membership.id, {
          validFrom: String(fields.get("validFrom")),
          validUntil: nullable(fields.get("validUntil")),
        });
      else
        await createMembership(teamId, {
          employeeId: String(fields.get("employeeId")),
          validFrom: String(fields.get("validFrom")),
          validUntil: nullable(fields.get("validUntil")),
        });
      await onSaved();
    } catch {
      setFailed(true);
      setSending(false);
    }
  }
  return (
    <form className="record-form team-inline-form" onSubmit={submit}>
      <div className="form-grid">
        {!membership ? (
          <label className="wide-field">
            Employee
            <select name="employeeId" required defaultValue="">
              <option value="" disabled>
                Select an employee
              </option>
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label>
          Valid from
          <input name="validFrom" type="date" required defaultValue={membership?.validFrom} />
        </label>
        <label>
          Valid until
          <input name="validUntil" type="date" defaultValue={membership?.validUntil ?? ""} />
        </label>
      </div>
      <FormActions failed={failed} sending={sending} onCancel={onCancel} />
    </form>
  );
}

function HoursForm({
  teamId,
  hours,
  onSaved,
  onCancel,
}: {
  teamId: string;
  hours?: WorkingHours;
  onSaved: () => Promise<void>;
  onCancel: () => void;
}) {
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSending(true);
    setFailed(false);
    const fields = new FormData(event.currentTarget);
    const input = {
      weekday: Number(fields.get("weekday")),
      startTime: String(fields.get("startTime")),
      endTime: String(fields.get("endTime")),
      effectiveFrom: String(fields.get("effectiveFrom")),
      effectiveUntil: nullable(fields.get("effectiveUntil")),
      active: fields.get("active") === "on",
    };
    try {
      await (hours ? updateWorkingHours(hours.id, input) : createWorkingHours(teamId, input));
      await onSaved();
    } catch {
      setFailed(true);
      setSending(false);
    }
  }
  return (
    <form className="record-form team-inline-form" onSubmit={submit}>
      <div className="form-grid">
        <label>
          Day
          <select name="weekday" defaultValue={hours?.weekday ?? 1}>
            {weekdayNames.map((day, index) => (
              <option key={day} value={index}>
                {day}
              </option>
            ))}
          </select>
        </label>
        <label className="toggle-field workforce-toggle">
          <input name="active" type="checkbox" defaultChecked={hours?.active ?? true} /> Active
          hours
        </label>
        <label>
          Start time
          <input
            name="startTime"
            type="time"
            required
            defaultValue={hours?.startTime.slice(0, 5)}
          />
        </label>
        <label>
          End time
          <input name="endTime" type="time" required defaultValue={hours?.endTime.slice(0, 5)} />
        </label>
        <label>
          Effective from
          <input name="effectiveFrom" type="date" required defaultValue={hours?.effectiveFrom} />
        </label>
        <label>
          Effective until
          <input name="effectiveUntil" type="date" defaultValue={hours?.effectiveUntil ?? ""} />
        </label>
      </div>
      <FormActions failed={failed} sending={sending} onCancel={onCancel} />
    </form>
  );
}

function SectionHeading({
  eyebrow,
  title,
  action,
  label,
}: {
  eyebrow: string;
  title: string;
  action?: () => void;
  label: string;
}) {
  return (
    <div className="property-heading">
      <div>
        <span className="eyebrow">{eyebrow}</span>
        <h3>{title}</h3>
      </div>
      {action ? (
        <button className="secondary-button small-action" onClick={action}>
          <Plus /> {label}
        </button>
      ) : null}
    </div>
  );
}
function FormActions({
  failed,
  sending,
  onCancel,
}: {
  failed: boolean;
  sending: boolean;
  onCancel: () => void;
}) {
  return (
    <>
      {failed ? (
        <p className="form-error">
          We couldn’t save this record. Check dates, times, and duplicates.
        </p>
      ) : null}
      <div className="form-actions">
        <button type="button" className="secondary-button" onClick={onCancel}>
          Cancel
        </button>
        <button className="primary-button compact-action" disabled={sending}>
          {sending ? <LoaderCircle className="spin" /> : null} Save
        </button>
      </div>
    </>
  );
}
function nullable(value: FormDataEntryValue | null) {
  const text = String(value ?? "").trim();
  return text || null;
}
function formatRange(from: string, until: string | null) {
  return `${shortDate(from)} – ${until ? shortDate(until) : "ongoing"}`;
}
function shortDate(value: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(`${value}T12:00:00`));
}
